import { create } from 'zustand';

import { isSupportAiAutoOpenSuppressed, suppressSupportAiAutoOpen } from './supportAiTriggerFlags';

/** One member per trigger that may open Support AI on the user's behalf. */
export type SupportAiTriggerId = 'ai_preview_support_intent';

export type SupportAiAutoOpenOutcome =
  | 'opened'
  | 'already_open'
  | 'already_fired'
  | 'user_dismissed';

interface SupportWidgetStore {
  open: boolean;
  pendingMessage: string | null;
  /** Trigger that queued pendingMessage; sent with that one message so Support AI can say why it opened. */
  pendingTrigger: SupportAiTriggerId | null;
  feedbackPopoverOpen: boolean;
  /** Trigger that owns the currently open panel; null whenever the user opened it themselves. */
  autoOpenTrigger: SupportAiTriggerId | null;
  /** Dedupe keys already fired this page load. */
  autoOpenedTriggerKeys: string[];
  autoOpenSuppressed: boolean;
  toggleOpen: () => void;
  openWithMessage: (message: string) => void;
  autoOpenWithMessage: (
    key: string,
    message: string,
    trigger: SupportAiTriggerId,
  ) => SupportAiAutoOpenOutcome;
  clearPendingMessage: () => void;
  setFeedbackPopoverOpen: (open: boolean) => void;
}

const CLOSED_WITH_NOTHING_QUEUED = {
  open: false,
  autoOpenTrigger: null,
  pendingMessage: null,
  pendingTrigger: null,
} as const;

export const useSupportWidgetStore = create<SupportWidgetStore>((set, get) => ({
  open: false,
  pendingMessage: null,
  pendingTrigger: null,
  feedbackPopoverOpen: false,
  autoOpenTrigger: null,
  autoOpenedTriggerKeys: [],
  autoOpenSuppressed: isSupportAiAutoOpenSuppressed(),
  toggleOpen: () =>
    set(({ open, autoOpenTrigger }) => {
      // Here rather than in one close handler so it covers the widget's own X and the TopBar toggle.
      if (open && autoOpenTrigger !== null) {
        suppressSupportAiAutoOpen();
        return { ...CLOSED_WITH_NOTHING_QUEUED, autoOpenSuppressed: true };
      }
      // Closing drops anything queued but not yet sent: the iframe may still have been loading,
      // and reopening later must not replay a message the user has already dismissed.
      return open ? CLOSED_WITH_NOTHING_QUEUED : { open: true, autoOpenTrigger: null };
    }),
  openWithMessage: (message: string) =>
    set({ open: true, pendingMessage: message, pendingTrigger: null, autoOpenTrigger: null }),
  autoOpenWithMessage: (key, message, trigger) => {
    const { open, autoOpenedTriggerKeys, autoOpenSuppressed } = get();
    if (autoOpenSuppressed) return 'user_dismissed';
    if (autoOpenedTriggerKeys.includes(key)) return 'already_fired';
    // Mark it fired anyway, so the trigger does not lie in wait for the moment they close the panel.
    if (open) {
      set({ autoOpenedTriggerKeys: [...autoOpenedTriggerKeys, key] });
      return 'already_open';
    }
    set({
      open: true,
      pendingMessage: message,
      pendingTrigger: trigger,
      autoOpenTrigger: trigger,
      autoOpenedTriggerKeys: [...autoOpenedTriggerKeys, key],
    });
    return 'opened';
  },
  clearPendingMessage: () => set({ pendingMessage: null, pendingTrigger: null }),
  setFeedbackPopoverOpen: (open: boolean) => set({ feedbackPopoverOpen: open }),
}));
